import { GridApiPro } from '@mui/x-data-grid-pro';
import { ScrollArrows } from '@schooly/components/data-grid';
import React, { FC, useCallback, useMemo } from 'react';

interface HeaderCellPosition {
  offsetLeft: number;
  width: number;
}

export interface AttendanceScrollArrowsProps {
  apiRef: React.MutableRefObject<GridApiPro | null> | null;
  containerRef: React.RefObject<HTMLDivElement>;
  pinnedColumns: number;
  colWidths: number[];
  onPageLeft?: () => void;
  onPageRight?: () => void;
}

export const AttendanceScrollArrows: FC<AttendanceScrollArrowsProps> = ({
  apiRef,
  containerRef,
  pinnedColumns,
  colWidths,
  onPageLeft,
  onPageRight,
}) => {
  const { pinnedColumnsWidth, headerCells } = useMemo(() => {
    const cells: HeaderCellPosition[] = [];
    let offsetLeft = 0;
    for (const width of colWidths.slice(pinnedColumns)) {
      cells.push({ offsetLeft, width });
      offsetLeft += width;
    }
    return {
      pinnedColumnsWidth: colWidths.slice(0, pinnedColumns).reduce((a, b) => a + b, 0),
      headerCells: cells,
    };
  }, [colWidths, pinnedColumns]);

  const canPaginateLeft = !!onPageLeft;
  const canPaginateRight = !!onPageRight;

  const showScrollLeftArrow = useCallback(
    () => (apiRef?.current?.getScrollPosition().left ?? 0) > 0,
    [apiRef],
  );

  const showScrollRightArrow = useCallback(() => {
    const leftPosition = apiRef?.current?.getScrollPosition().left ?? 0;
    const scrollWidth = headerCells.reduce((sum, cell) => sum + cell.width, 0);
    const containerWidth = containerRef.current?.offsetWidth ?? 0;
    const widthOfVisibleArea = containerWidth - pinnedColumnsWidth;
    const leftMaxPosition = scrollWidth - widthOfVisibleArea;

    return leftMaxPosition > leftPosition * 1.01;
  }, [apiRef, containerRef, headerCells, pinnedColumnsWidth]);

  const showLeftArrow = useCallback(() => {
    return showScrollLeftArrow() || canPaginateLeft;
  }, [canPaginateLeft, showScrollLeftArrow]);

  const showRightArrow = useCallback(() => {
    return showScrollRightArrow() || canPaginateRight;
  }, [canPaginateRight, showScrollRightArrow]);

  const handleArrowClick = (direction: 'left' | 'right') => () => {
    if (!apiRef?.current) return;

    switch (direction) {
      case 'left':
        if (!showScrollLeftArrow()) {
          onPageLeft?.();
          return;
        }
        break;
      case 'right':
        if (!showScrollRightArrow()) {
          onPageRight?.();
          return;
        }
        break;
    }

    const leftPosition = apiRef.current.getScrollPosition().left ?? 0;

    const scrollTo =
      direction === 'right'
        ? headerCells.find((cell) => cell.offsetLeft > Math.floor(leftPosition))
        : [...headerCells].reverse().find((cell) => cell.offsetLeft < Math.floor(leftPosition));

    if (!scrollTo) return;

    apiRef.current.scroll({ left: scrollTo.offsetLeft });
  };
  return (
    <ScrollArrows
      onArrowClick={handleArrowClick}
      shouldShowRightArrow={showRightArrow}
      shouldShowLeftArrow={showLeftArrow}
      apiRef={apiRef}
    />
  );
};
